import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  GoHighLevelIntegration,
  MailchimpIntegration,
  WordPressIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { getPropsFromIntegration } from './utils'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { ReactNode } from 'react'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useDeleteExternalIntegration } from 'hooks/queries/externalIntegrations/useDeleteExternalIntegration'
import { useModals } from 'almanac-ui/Modal'
import { MailchimpSettingsModal } from '../components/MailchimpSettingsModal/MailchimpSettingsModal'
import { GoHighLevelConfigurationModal } from 'components/GoHighLevelConfigurationModal/GoHighLevelConfigurationModal'
import { WordPressSettingsModal } from '../components/WordPressSettingsModal/WordPressSettingsModal'

export interface ConnectedAccountCardProps {
  avatarUrl?: string
  icon: ReactNode
  details?: string
  name?: string
  onDisconnectAccount: () => void
  onRefreshConnection: (options?: { popup?: boolean }) => void
  isDeleting: boolean
  onConfigureClick: () => void
  showSettings: boolean
}

export const useConnectedAccountCardProps = (
  integration?: ExternalIntegration,
  { showSuccessToast = true, showErrorToast = true } = {},
): ConnectedAccountCardProps => {
  const { openModal } = useModals()
  const { initiateOauth } = useInitiateOauth()
  const { connect } = useConnectAccountPopup()

  const providerName = integration ? ProviderToName[integration.provider] : ''

  const { mutateAsync: onDisconnectAccount, isLoading: isDeleting } = useDeleteExternalIntegration(
    integration?.id || 0,
    {
      onSuccess: () => {
        if (showSuccessToast) {
          showToastMessage(`Successfully disconnected ${providerName} account!`, 'success')
        }
      },
      onError: () => {
        if (showErrorToast) {
          showToastMessage(`Unable to disconnect ${providerName} account`, 'error')
        }
      },
    },
  )

  if (!integration) {
    return {
      icon: null,
      name: '',
      onDisconnectAccount: () => {},
      onRefreshConnection: () => {},
      isDeleting: false,
      showSettings: false,
      onConfigureClick: () => {},
    }
  }

  const showSettings =
    integration.provider === ExternalIntegrationEnum.GoHighLevel ||
    integration.provider === ExternalIntegrationEnum.Mailchimp ||
    integration.provider === ExternalIntegrationEnum.WordPress

  const onConfigureClick = () => {
    switch (integration.provider) {
      case ExternalIntegrationEnum.Mailchimp:
        return openModal(MailchimpSettingsModal, { integration: integration as MailchimpIntegration })
      case ExternalIntegrationEnum.GoHighLevel:
        return openModal(GoHighLevelConfigurationModal, { integration: integration as GoHighLevelIntegration })
      case ExternalIntegrationEnum.WordPress:
        return openModal(WordPressSettingsModal, { integration: integration as WordPressIntegration })
    }
  }

  const onRefreshConnection = async (options?: { popup?: boolean }) => {
    const { popup } = options || {}

    const shopDomain =
      integration.additional_data && 'shop_domain' in integration.additional_data
        ? (integration.additional_data.shop_domain as string)
        : undefined

    if (popup) {
      const popup = await initiateOauth(
        integration.provider,
        integration.id as number,
        '_blank',
        'popup=true,width=600,height=780',
        shopDomain,
      )

      if (popup) {
        connect(popup, {
          onIntegrationAuthCreated: () => {
            showToastMessage('Connection refreshed successfully!', 'success')
          },
        })
      }
    } else {
      initiateOauth(integration.provider, integration.id as number, undefined, undefined, shopDomain)
    }
  }

  return {
    ...getPropsFromIntegration(integration),
    onDisconnectAccount,
    onRefreshConnection,
    isDeleting,
    onConfigureClick,
    showSettings,
  }
}
